import graphql__b6eb3abf6a6d56765619 from "./__generated__/IncomingInvoicesViewQuery.graphql.ts";
import graphql__7a05a277e6a15a00740e from "./__generated__/IncomingInvoicesView_invoices.graphql.ts";import * as React from "react";
import {useEffect, useMemo, useState, useTransition} from "react";
import {graphql} from "react-relay";
import {useLazyLoadQuery, usePaginationFragment} from "react-relay/hooks";
import {useNavigate, useSearchParams} from "react-router-dom";
import {useIntl} from "react-intl";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheck, faFileMagnifyingGlass} from "@fortawesome/pro-regular-svg-icons";
import {faFilePdf} from "@fortawesome/pro-solid-svg-icons";
import Panel from "./presentational/Panel";
import Table from "./presentational/table/Table";
import TableHeader, {TextAlignment} from "./presentational/table/TableHeader";
import TableData from "./presentational/table/TableData";
import ActionButton from "./presentational/button/ActionButton";
import LoadMoreButton from "./presentational/LoadMoreButton";
import StatusBadge from "./presentational/StatusBadge";
import ConfirmSupplierServicePeriodProposalsDialog from "./dialog/ConfirmSupplierServicePeriodProposalsDialog";
import {formatAmount} from "../helper/revenueFormat";
import {EXPENSE_CATEGORIES} from "../helper/expenseLabels";
import {formatAmountInCurrency, formatInvoiceMonth, formatServicePeriod} from "../helper/expenseInvoiceFormat";
import {formatDay} from "../helper/dateFormat";
import {
    EXPENSE_INVOICES_PATH,
    ExpenseInvoiceListFilter,
    expenseInvoiceFilterFromSearchParams,
    expenseInvoiceFilterToGraphQL,
    expenseInvoiceFilterToSearchParams,
    isServiceMonth,
    NO_VALUE,
    SERVICE_PERIOD_STATUSES,
} from "../helper/expenseInvoiceFilter";
import type {IncomingInvoicesViewQuery as IncomingInvoicesViewQueryType} from "./__generated__/IncomingInvoicesViewQuery.graphql";
import type {IncomingInvoicesView_invoices$key} from "./__generated__/IncomingInvoicesView_invoices.graphql";

const PAGE_SIZE = 100;

/**
 * The "Invoices" area of the expenses: all incoming invoices, the latest first. The filter lives
 * in the URL (see expenseInvoiceFilter.ts), so other views can link to a filtered list.
 */
const IncomingInvoicesView: React.FC = () => {
    const intl = useIntl();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isPending, startTransition] = useTransition();
    const filter = useMemo(() => expenseInvoiceFilterFromSearchParams(searchParams), [searchParams]);
    const [searchTerm, setSearchTerm] = useState(filter.q ?? '');
    // Browsers without a month picker show a text field, so a month is typed character by
    // character; only a complete month (or none) goes to the URL.
    const [monthInput, setMonthInput] = useState(filter.month ?? '');
    const [fetchKey, setFetchKey] = useState(0);
    const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

    const data = useLazyLoadQuery<IncomingInvoicesViewQueryType>(
        graphql__b6eb3abf6a6d56765619,
        {filter: expenseInvoiceFilterToGraphQL(filter)},
        {fetchPolicy: 'store-and-network', fetchKey}
    );

    const suppliers = useMemo(
        () => [...data.suppliers].sort((a, b) => a.name.localeCompare(b.name)),
        [data.suppliers]
    );
    // Confirming acts on all proposals of the supplier, so it is only offered where the list is
    // about exactly these.
    const supplierWithProposals = filter.status === 'PROPOSED' && !filter.category && !filter.month && !filter.q
        ? suppliers.find(supplier => supplier.supplierIdentifier === filter.supplier && supplier.proposedServicePeriodCount > 0)
        : undefined;

    const closeConfirmDialog = (confirmed: boolean) => {
        setIsConfirmDialogOpen(false);
        if (confirmed) {
            startTransition(() => setFetchKey(key => key + 1));
        }
    };

    // Builds on the current search params, not on the filter of the last render: the debounced
    // search term must not undo a filter changed while it was waiting.
    const updateFilter = (changes: Partial<ExpenseInvoiceListFilter>) => {
        startTransition(() => {
            setSearchParams(
                currentSearchParams => expenseInvoiceFilterToSearchParams({...expenseInvoiceFilterFromSearchParams(currentSearchParams), ...changes}),
                {replace: true}
            );
        });
    };

    // The search term goes to the URL only after typing paused, so not every key stroke queries the API.
    useEffect(() => {
        const term = searchTerm.trim();
        if (term === (filter.q ?? '')) {
            return;
        }
        const timeout = setTimeout(() => updateFilter({q: term || undefined}), 300);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTerm]);

    // A month set from outside, e.g. by a link or the reset button, replaces what was typed.
    useEffect(() => {
        setMonthInput(filter.month ?? '');
    }, [filter.month]);

    const handleMonthInputChange = (value: string) => {
        setMonthInput(value);
        if (value === '' || isServiceMonth(value)) {
            updateFilter({month: value || undefined});
        }
    };

    const selectClassName ='border border-gray-300 rounded pl-2 pr-8 py-1.5 text-sm text-gray-900 max-w-full';
    const hasFilter = expenseInvoiceFilterToSearchParams(filter).toString() !== '';

    return (
        <div className="space-y-6 px-4 sm:px-6 lg:px-0">
            <div className="flex flex-wrap items-center gap-3">
                <select
                    value={filter.supplier ?? ''}
                    onChange={event => updateFilter({supplier: event.target.value || undefined})}
                    className={selectClassName + ' w-64'}
                    aria-label={intl.formatMessage({id: 'view.incomingInvoices.filter.supplier'})}
                >
                    <option value="">{intl.formatMessage({id: 'view.incomingInvoices.filter.allSuppliers'})}</option>
                    {suppliers.map(supplier => (
                        <option key={supplier.id} value={supplier.supplierIdentifier}>{supplier.name}</option>
                    ))}
                </select>
                <select
                    value={filter.category ?? ''}
                    onChange={event => updateFilter({category: (event.target.value || undefined) as ExpenseInvoiceListFilter['category']})}
                    className={selectClassName}
                    aria-label={intl.formatMessage({id: 'view.incomingInvoices.filter.category'})}
                >
                    <option value="">{intl.formatMessage({id: 'view.incomingInvoices.filter.allCategories'})}</option>
                    {EXPENSE_CATEGORIES.map(category => (
                        <option key={category} value={category}>{intl.formatMessage({id: `expense.category.${category}`})}</option>
                    ))}
                    <option value={NO_VALUE}>{intl.formatMessage({id: 'view.suppliers.unassigned'})}</option>
                </select>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    {intl.formatMessage({id: 'view.incomingInvoices.filter.month'})}
                    <input
                        type="month"
                        value={monthInput}
                        onChange={event => handleMonthInputChange(event.target.value)}
                        placeholder={intl.formatMessage({id: 'view.incomingInvoices.filter.monthPlaceholder'})}
                        className={selectClassName.replace('pr-8', 'pr-2')}
                        title={intl.formatMessage({id: 'view.incomingInvoices.filter.monthHint'})}
                    />
                </label>
                <select
                    value={filter.status ?? ''}
                    onChange={event => updateFilter({status: (event.target.value || undefined) as ExpenseInvoiceListFilter['status']})}
                    className={selectClassName}
                    aria-label={intl.formatMessage({id: 'view.incomingInvoices.filter.status'})}
                >
                    <option value="">{intl.formatMessage({id: 'view.incomingInvoices.filter.allStatuses'})}</option>
                    {SERVICE_PERIOD_STATUSES.map(status => (
                        <option key={status} value={status}>{intl.formatMessage({id: `expense.servicePeriodStatus.${status}`})}</option>
                    ))}
                    <option value={NO_VALUE}>{intl.formatMessage({id: 'expense.servicePeriod.invoiceMonth'})}</option>
                </select>
                <input
                    type="search"
                    value={searchTerm}
                    onChange={event => setSearchTerm(event.target.value)}
                    placeholder={intl.formatMessage({id: 'view.incomingInvoices.filter.search'})}
                    className="border border-gray-300 rounded px-3 py-1.5 text-sm w-72 max-w-full"
                />
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                    <input type="checkbox" checked={filter.deleted === true} onChange={event => updateFilter({deleted: event.target.checked || undefined})}/>
                    {intl.formatMessage({id: 'view.incomingInvoices.filter.deleted'})}
                </label>
                {hasFilter && (
                    <button
                        type="button"
                        className="text-sm text-gray-500 hover:text-gray-900 underline"
                        onClick={() => {
                            setSearchTerm('');
                            setMonthInput('');
                            startTransition(() => setSearchParams(new URLSearchParams(), {replace: true}));
                        }}
                    >
                        {intl.formatMessage({id: 'view.incomingInvoices.filter.reset'})}
                    </button>
                )}
                {supplierWithProposals && (
                    <ActionButton
                        icon={faCheck}
                        label={intl.formatMessage({id: 'view.incomingInvoices.action.confirmAll'}, {count: supplierWithProposals.proposedServicePeriodCount})}
                        additionalClassname="ml-auto!"
                        onClick={() => setIsConfirmDialogOpen(true)}
                    />
                )}
            </div>
            <ExpenseInvoiceTable queryRef={data} searchParams={searchParams} isPending={isPending} serviceMonth={filter.month}/>
            {isConfirmDialogOpen && supplierWithProposals && (
                <ConfirmSupplierServicePeriodProposalsDialog supplier={supplierWithProposals} onClose={closeConfirmDialog}/>
            )}
        </div>
    );
};

type ExpenseInvoiceTableProps = {
    queryRef: IncomingInvoicesView_invoices$key
    searchParams: URLSearchParams
    isPending: boolean
    serviceMonth?: string // when set, the list is filtered by this month and shows the share counting in it
};

const ExpenseInvoiceTable: React.FC<ExpenseInvoiceTableProps> = ({queryRef, searchParams, isPending, serviceMonth}) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const {data, hasNext, loadNext, isLoadingNext} = usePaginationFragment(
        graphql__7a05a277e6a15a00740e,
        queryRef
    );

    const openInvoice = (event: React.MouseEvent, id: string) => {
        const query = searchParams.toString();
        const to = EXPENSE_INVOICES_PATH + '/' + encodeURIComponent(id) + (query !== '' ? '?' + query : '');
        if (event.shiftKey || event.metaKey) {
            event.preventDefault();
            window.open(to);
        } else {
            navigate(to);
        }
    };

    const invoices = data.expenseInvoices;
    // Only a complete list can be totalled
    const totalInMonth = serviceMonth && !hasNext
        ? invoices.edges.reduce((sum, {node}) => sum + (node.netAmountInServiceMonth ?? 0), 0)
        : null;

    return (
        <>
            <p className="text-sm text-gray-500">
                {intl.formatMessage({id: 'view.incomingInvoices.summary'}, {count: invoices.totalCount})}
            </p>
            {serviceMonth && (
                <p className="text-sm text-gray-500">
                    {intl.formatMessage({id: 'view.incomingInvoices.serviceMonthHint'})}
                    {totalInMonth !== null && ' ' + intl.formatMessage({id: 'view.incomingInvoices.serviceMonthTotal'}, {amount: formatAmount(totalInMonth)})}
                </p>
            )}
            <Panel fullWidthChildren={true}>
                {invoices.edges.length === 0 ? (
                    <p className="px-4 py-5 sm:p-6 text-sm text-gray-500">{intl.formatMessage({id: 'view.incomingInvoices.empty'})}</p>
                ) : (
                    <div className={'overflow-x-auto' + (isPending ? ' opacity-60' : '')}>
                        <Table>
                            <thead>
                                <tr>
                                    <TableHeader className="pl-4 sm:pl-6 pr-3">{intl.formatMessage({id: 'view.incomingInvoices.col.supplier'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.incomingInvoices.col.invoiceNumber'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.incomingInvoices.col.invoiceDate'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.incomingInvoices.col.servicePeriod'})}</TableHeader>
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.incomingInvoices.col.expenseCategory'})}</TableHeader>
                                    <TableHeader className="px-3" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.incomingInvoices.col.netAmount'})}</TableHeader>
                                    {serviceMonth && (
                                        <TableHeader className="px-3" textAlignment={TextAlignment.Right}>{intl.formatMessage({id: 'view.incomingInvoices.col.shareInMonth'})}</TableHeader>
                                    )}
                                    <TableHeader className="px-3">{intl.formatMessage({id: 'view.incomingInvoices.col.paidDate'})}</TableHeader>
                                    <TableHeader className="pl-3 pr-4 sm:pr-6"/>
                                </tr>
                            </thead>
                            <tbody>
                                {invoices.edges.map(({node: invoice}, index) => {
                                    const servicePeriod = formatServicePeriod(intl, invoice.servicePeriodStart, invoice.servicePeriodEnd);
                                    return (
                                        <tr
                                            key={invoice.id}
                                            onClick={event => openInvoice(event, invoice.id)}
                                            className={(index % 2 === 0 ? 'bg-white' : 'bg-gray-50') + ' hover:bg-gray-100 group cursor-pointer' + (invoice.deletedInFastBillAt ? ' text-gray-400 line-through' : '')}
                                        >
                                            <TableData className="pl-4 sm:pl-6 pr-3 font-medium text-gray-900">{invoice.supplierName}</TableData>
                                            <TableData className="px-3 break-all">{invoice.invoiceNumber ?? '–'}</TableData>
                                            <TableData className="px-3 whitespace-nowrap">{formatDay(intl, invoice.invoiceDate)}</TableData>
                                            <TableData className="px-3 whitespace-nowrap">
                                                {servicePeriod === null ? (
                                                    <span className="text-gray-400" title={intl.formatMessage({id: 'expense.servicePeriod.invoiceMonth'})}>{formatInvoiceMonth(intl, invoice.invoiceDate)}</span>
                                                ) : (
                                                    <>
                                                        <span className="mr-2">{servicePeriod}</span>
                                                        {invoice.servicePeriodStatus && (
                                                            <ServicePeriodStatusBadge status={invoice.servicePeriodStatus}/>
                                                        )}
                                                    </>
                                                )}
                                            </TableData>
                                            <TableData className="px-3">
                                                {invoice.expenseCategory ? (
                                                    <span title={invoice.expenseCategoryOverride ? intl.formatMessage({id: 'expense.category.overridden.title'}) : undefined}>
                                                        {intl.formatMessage({id: `expense.category.${invoice.expenseCategory}`})}
                                                        {invoice.expenseCategoryOverride && (
                                                            <span className="ml-1.5 text-xs text-gray-400">{intl.formatMessage({id: 'expense.category.overridden'})}</span>
                                                        )}
                                                    </span>
                                                ) : (
                                                    <span className="text-gray-300">–</span>
                                                )}
                                            </TableData>
                                            <TableData className="px-3 whitespace-nowrap" textAlignment={TextAlignment.Right}>
                                                <div>{formatAmount(invoice.netAmountInEuro)}</div>
                                                {invoice.currency !== 'EUR' && (
                                                    <div className="text-xs text-gray-500">{formatAmountInCurrency(intl, invoice.netAmount, invoice.currency)}</div>
                                                )}
                                            </TableData>
                                            {serviceMonth && (
                                                <TableData className="px-3 whitespace-nowrap font-medium text-gray-900" textAlignment={TextAlignment.Right}>
                                                    {invoice.netAmountInServiceMonth !== null && invoice.netAmountInServiceMonth !== undefined ? formatAmount(invoice.netAmountInServiceMonth) : '–'}
                                                </TableData>
                                            )}
                                            <TableData className="px-3 whitespace-nowrap">{formatDay(intl, invoice.paidDate)}</TableData>
                                            <TableData className="pl-3 pr-4 sm:pr-6 whitespace-nowrap" textAlignment={TextAlignment.Right}>
                                                {invoice.hasDocument && (
                                                    <FontAwesomeIcon icon={faFilePdf} className="text-gray-400" title={intl.formatMessage({id: 'view.incomingInvoices.hasDocument'})}/>
                                                )}
                                                <ActionButton
                                                    icon={faFileMagnifyingGlass}
                                                    title={intl.formatMessage({id: 'view.incomingInvoices.action.show'})}
                                                    additionalClassname="invisible group-hover:visible"
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        openInvoice(event, invoice.id);
                                                    }}
                                                />
                                            </TableData>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Panel>
            {hasNext && <LoadMoreButton onClick={() => loadNext(PAGE_SIZE)} isLoadingNext={isLoadingNext}/>}
        </>
    );
};

/**
 * Marks a proposed service period, the only state that still asks for action. Confirmed periods
 * need no mark.
 */
export const ServicePeriodStatusBadge: React.FC<{status: string}> = ({status}) => {
    const intl = useIntl();
    if (status !== 'PROPOSED') {
        return null;
    }
    return (
        <StatusBadge
            value={status}
            label={intl.formatMessage({id: `expense.servicePeriodStatus.${status}`})}
            states={{yellow: ['PROPOSED']}}
        />
    );
};

export default IncomingInvoicesView;
